import type { LanguageServiceProvider, LanguageServiceEngine } from './languageServiceProvider';
import { noopLanguageServiceProvider } from './noopLanguageServiceProvider';
import { mssqlLanguageServiceProvider } from './mssqlLanguageServiceProvider';
import { pgLanguageServiceProvider } from './pgLanguageServiceProvider';
import { mockLanguageServiceProvider } from './mockLanguageServiceProvider';
import { sqliteLanguageServiceProvider } from './sqliteLanguageServiceProvider';
import { mysqlLanguageServiceProvider } from './mysqlLanguageServiceProvider';
import { oracleLanguageServiceProvider } from './oracleLanguageServiceProvider';
import { kustoLanguageServiceProvider } from './kustoLanguageServiceProvider';
import { shouldSkipForeignEngineLoad } from '../connectionEngineGuard';

const providers = new Map<LanguageServiceEngine, LanguageServiceProvider>();

/**
 * Every provider's `connectForLanguageServices` — the connect-time schema load
 * of all six engines, and the PostgreSQL language-server context switch —
 * passes the wrong-engine guard first: a connection the store knows to be of
 * another engine is never introspected with this provider's dialect. Applied
 * here, where every provider is obtained, so no engine can miss it. A skipped
 * connect reports `false` (not connected) and leaves the document untouched.
 */
function withForeignEngineGuard(provider: LanguageServiceProvider): LanguageServiceProvider {
  return {
    ...provider,
    connectForLanguageServices: async (uri, connectionInfo) => {
      if (shouldSkipForeignEngineLoad(connectionInfo.connectionId, provider.engine, 'provider-connect')) {
        return false;
      }
      return provider.connectForLanguageServices(uri, connectionInfo);
    },
  };
}

export const registerLanguageServiceProvider = (provider: LanguageServiceProvider): void => {
  providers.set(provider.engine, withForeignEngineGuard(provider));
};

export const getLanguageServiceProvider = (engine?: LanguageServiceEngine): LanguageServiceProvider => {
  const resolvedEngine = engine ?? 'mssql';
  return (
    providers.get(resolvedEngine) ??
    providers.get('mssql') ??
    noopLanguageServiceProvider
  );
};

export const listLanguageServiceProviders = (): LanguageServiceEngine[] => {
  return Array.from(providers.keys());
};

// Register built-in providers. mssql is the in-house provider (Plan 20 §10
// step 6 — mssql documents left the SSTS LSP); it also doubles as the
// unknown-engine fallback above, which is safe because it produces zero
// SSTS bridge traffic.
//
// Completion: `mock` is the ONLY provider whose `getCompletionItems` is
// live — it's the sole caller reachable from `mockLspBridge.ts`. Every real
// engine (mssql, pgsql, mysql, oracle, sqlite, kusto) gets completion from
// its dialect-native resolver instead (`resolverRegistry.ts`), so those
// providers' `getCompletionItems` arms are intentionally empty stubs
// (Workstream A — legacy per-engine completion layer retirement). They stay
// registered here because they still own hover / signature-help /
// connect-lifecycle / schema-cache warm-up for their engine.
registerLanguageServiceProvider(mssqlLanguageServiceProvider);
registerLanguageServiceProvider(pgLanguageServiceProvider);
registerLanguageServiceProvider(mockLanguageServiceProvider);
registerLanguageServiceProvider(sqliteLanguageServiceProvider);
registerLanguageServiceProvider(mysqlLanguageServiceProvider);
registerLanguageServiceProvider(oracleLanguageServiceProvider);
registerLanguageServiceProvider(kustoLanguageServiceProvider);
